import React, { useEffect, useMemo, useRef, useState } from "react";

import { CloseButton, Form } from "react-bootstrap";

import { debounce } from "lodash";
import { ReactSVG } from "react-svg";

import {
  ArtCentreArtistFragment,
  SearchParticipantsQueryVariables,
  useSearchParticipantsLazyQuery,
} from "@api/graphql";
import { SearchIcon } from "@assets/icons";
import { getDisplayNameForParticipant } from "@util/index";

import ParticipantDropdown, { ParticipantWithSinglePseudonym } from "./ParticipantDropdown";

import styles from "./ParticipantSearch.module.scss";

interface ParticipantSearchProps {
  passedArtist?: ArtCentreArtistFragment;
  onParticipantSelect: (participant: ParticipantWithSinglePseudonym | null) => void;
  disabled?: boolean;
  variables?: SearchParticipantsQueryVariables;
}

const DEBOUNCE_DELAY = 200;
const LEAST_CHARACTER_TO_START_SEARCHING = 2;

const ParticipantSearch: React.FC<ParticipantSearchProps> = ({
  passedArtist,
  onParticipantSelect,
  disabled,
  variables,
}) => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState<ParticipantWithSinglePseudonym | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  const queryVariable = useMemo(() => {
    const filter_by = variables?.filter_by
      ? Array.isArray(variables?.filter_by)
        ? variables.filter_by
        : [variables.filter_by]
      : [];

    const searchByParticipantIdOrName = !isNaN(parseInt(searchQuery))
      ? { field: "participant_id", eq: searchQuery }
      : { field: "partialName", iLike: searchQuery };

    const shouldFilterByParticipantId = filter_by.some(
      (filter) => filter.field === "filterByParticipantId" && filter.eq === "true",
    );

    const updatedFilters = shouldFilterByParticipantId
      ? [...filter_by.filter((filter) => filter.field !== "filterByParticipantId"), searchByParticipantIdOrName]
      : [...filter_by, { field: "partialName", iLike: searchQuery }];

    return {
      ...variables,
      filter_by: updatedFilters,
    };
  }, [searchQuery, variables]);

  const placeholderText = useMemo(() => {
    const filter_by = variables?.filter_by;
    const hasfilterByParticipantId =
      Array.isArray(filter_by) && filter_by.some((filter) => filter.field === "filterByParticipantId");
    return hasfilterByParticipantId ? "Search by participant id, name, pseudonym..." : "Search by name, pseudonym...";
  }, [variables]);

  const [searchParticipants, { data, loading }] = useSearchParticipantsLazyQuery();

  const debouncedSearch = useMemo(
    () =>
      debounce((searchQuery: string) => {
        if (searchQuery.length > LEAST_CHARACTER_TO_START_SEARCHING) {
          searchParticipants({ variables: queryVariable });
        }
      }, DEBOUNCE_DELAY),
    [queryVariable, searchParticipants],
  );

  useEffect(() => {
    if (passedArtist && passedArtist.artist_name) {
      const formattedName = `${passedArtist.artist_id} ${passedArtist.artist_name}${passedArtist.pseudonym ? ` (${passedArtist.pseudonym})` : ""}`;
      setSearchQuery(formattedName);
      setSelectedParticipant(passedArtist as unknown as ParticipantWithSinglePseudonym);
    }
  }, [passedArtist]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled || selectedParticipant) {
      return;
    }
    const query = e.target.value;
    setSearchQuery(query);
    setDropdownVisible(query.length > 0);
  };

  useEffect(() => {
    debouncedSearch(searchQuery);
    return () => debouncedSearch.cancel();
  }, [debouncedSearch, searchQuery]);

  const handleClearInput = () => {
    if (disabled) {
      return;
    }
    setSearchQuery("");
    setDropdownVisible(false);
    setSelectedParticipant(null);
    onParticipantSelect(null);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleSearchedParticipantSelect = (participant: ParticipantWithSinglePseudonym) => {
    setDropdownVisible(false);
    setSelectedParticipant(participant);
    const displayName = getDisplayNameForParticipant(participant);
    setSearchQuery(displayName);
    onParticipantSelect(participant);
  };

  const handleWrapperClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget && !selectedParticipant) {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  };

  return (
    <Form.Group className={styles.searchInputWrapper} onClick={handleWrapperClick}>
      <ReactSVG src={SearchIcon} className={styles.blankBlock} />
      <Form.Control
        type="text"
        placeholder={placeholderText}
        value={searchQuery}
        onChange={handleInputChange}
        className={styles.searchInput}
        ref={inputRef}
        disabled={disabled || !!selectedParticipant}
      />
      {loading ? (
        <div className={`spinner-border ${styles.blankBlock}`} role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      ) : searchQuery && !disabled ? (
        <CloseButton onClick={handleClearInput} className={styles.blankBlock} />
      ) : (
        <div className={styles.blankBlock}></div>
      )}
      <ParticipantDropdown
        filteredResults={data?.searchParticipants || []}
        onParticipantSelect={handleSearchedParticipantSelect}
        visible={
          dropdownVisible &&
          searchQuery.length > 0 &&
          (data?.searchParticipants || []).length > 0 &&
          !selectedParticipant
        }
      />
    </Form.Group>
  );
};

export default ParticipantSearch;
